import { isArticleTileViewModel } from '@rocco/ui/tile-article/view-models/article';
import { isProfessionalTileViewModel } from '@rocco/ui/tile-professional/view-models/professional';
import { isProjectTileViewModel } from '@rocco/ui/tile-project/view-models/project';
import { isProductTileViewModel } from '@rocco/ui/tile-product/view-models/product';
import type { TileableModel } from '@rocco/ui/tile-shared/view-models/tile';
import type { TileEnquiryConfig } from '~/modules/enquiry/hooks/use-tile-enquiry';
import type { ShareOptions } from '~/modules/social-sharing/utils/sharing';
import type {
    TileActionInfo,
    TileVisitWebsiteConfig,
} from '../components/TileActionPanel';
import { buildTileActionInfo } from './tile-action-info';
import { buildTileVisitWebsite } from './tile-visit-website';
import { getPinType } from './tile-pin-type';
import type { PinItemType } from '../type';

/** Everything the action panel needs about an item, in one object. */
export interface TileActionProps {
    pinId: number;
    pinType: PinItemType;
    enquiry: TileEnquiryConfig | null;
    share: ShareOptions | null;
    visitWebsite: TileVisitWebsiteConfig | null;
    tileInfo: TileActionInfo;
}

/**
 * The tile's enquiry target, or null when it has none.
 *
 * A professional enquires about itself. A product or project enquires its
 * supplier about the item. An article enquires the practice that wrote it.
 *
 * All but the first need that professional's id and link — without them the
 * tile cannot address the enquiry, so it offers no Enquire at all rather than
 * one that fails on click.
 */
const buildTileEnquiry = (model: TileableModel): TileEnquiryConfig | null => {
    if (isProfessionalTileViewModel(model)) {
        return {
            itemType: 'professional',
            siteTreeID: model.id,
            professionalId: model.id,
            professionalName: model.title,
            professionalLink: model.link,
            itemTitle: model.title,
        };
    }

    // An article enquires the practice that wrote it, about them rather than
    // about the article — there is nothing to quote on a piece of writing, but
    // a reader who has just read one has a reason to talk to whoever wrote it.
    if (isArticleTileViewModel(model)) {
        const author = model.professional;
        if (!author?.id || !author.link) return null;
        return {
            itemType: 'professional',
            siteTreeID: author.id,
            professionalId: author.id,
            professionalName: author.name ?? '',
            professionalLink: author.link,
            itemTitle: author.name ?? '',
        };
    }

    if (!isProductTileViewModel(model) && !isProjectTileViewModel(model)) {
        return null;
    }

    const professional = model.professional;
    if (!professional?.id || !professional.link) return null;

    return {
        itemType: isProductTileViewModel(model) ? 'product' : 'project',
        siteTreeID: model.id,
        professionalId: professional.id,
        professionalName: professional.name ?? '',
        professionalLink: professional.link,
        itemTitle: model.title,
        itemImage: model.images?.[0]?.src,
    };
};

/**
 * The tile's share target. Tiles hold a site-relative link; the panel resolves
 * it against the origin when the visitor actually shares.
 */
const buildTileShare = (model: TileableModel): ShareOptions | null => {
    if (!('link' in model) || !model.link) return null;
    return { url: model.link, title: 'title' in model ? model.title : '' };
};

/**
 * Every action config a tile's hover panel needs, derived from the view model
 * it already has. One helper because the panel is now the same on every
 * surface: a slider tile, a browse tile and a detail tile should not each
 * decide separately what a product tile can offer.
 *
 * Each action self-hides when the model can't supply it, so a surface spreads
 * this whatever it is showing:
 *
 * ```tsx
 * <TileOverlayWithFollowSelector {...buildTileActionProps(model)} />
 * ```
 */
export const buildTileActionProps = (
    model: TileableModel,
): TileActionProps => ({
    pinId: model.id,
    pinType: getPinType(model),
    enquiry: buildTileEnquiry(model),
    share: buildTileShare(model),
    visitWebsite: buildTileVisitWebsite(model),
    tileInfo: buildTileActionInfo(model),
});
